import { type AddHighlightOptions } from '@databases/highlights';

/**
 * This element is created and injected into the scrollable inner container
 * so it can stretch and match the width and height, effectively getting "free"
 * scrolling, rather than us trying to position elements and match them on scroll
 * and introducing a rubber band.
 */
export const SELECTIONS_CONTAINER_ELEMENT_ID = 'selections-overlay-container';

/**
 * The element that represents the current focus and is drawn on top of the associated
 * cell.
 */
export const SELECTIONS_FOCUS_BOX_ELEMENT_ID = 'selections-focus-box';

/**
 * The unique DOM ID for the focus box highlight.
 */
export const SELECTIONS_FOCUS_BOX_HIGHLIGHT_ID = 'selections-focus-box-highlight';
/**
 * This element is the main container that we base our highlights off of.
 * For highlights, we inject a container and match the size, allow pointer events
 * to passthrough, etc. When this is not mounted, we cannot have our machine running.
 */
export const SCROLLABLE_INNER_CONTAINER_ID = 'scrollable-inner-container';

/**
 * The main react application. This is used to listen to for mounting
 * highlights when the app is ready.
 */
export const APP_ELEMENT_ID = 'app';

/**
 * The unique DOM ID for the fill handle indicator (the small square/circle
 * at the bottom right of the active selection).
 */
export const SELECTIONS_FILL_HANDLE_HIGHLIGHT_ID = 'selections-fill-handle';

// ---------------------------------------------------------------------------
// Global Layer Z-Indices (Stacking order of the actual canvas layers)
// ---------------------------------------------------------------------------

export const PINNED_FIELD_BODY_CELL_Z_INDEX = 4;
const SELECTION_CHROME_LAYER_Z_INDEX = PINNED_FIELD_BODY_CELL_Z_INDEX + 1;
export const SCROLLING_HIGHLIGHTS_LAYER_Z_INDEX = 1;
export const SCROLLING_INTERACTION_LAYER_Z_INDEX = 3;

/**
 * The bottom-most layer. Renders the semi-transparent blue backgrounds for
 * all active selection ranges.
 */
export const ROW_HIGHLIGHTS_LAYER_Z_INDEX = 10;

/**
 * Places the shared fixed-horizontal surface above sticky frozen field cells.
 */
export const FIXED_HIGHLIGHTS_SURFACE_Z_INDEX = SELECTION_CHROME_LAYER_Z_INDEX;

/**
 * Renders the semi-transparent blue backgrounds for active fixed selections.
 */
export const FIXED_HIGHLIGHTS_LAYER_Z_INDEX = FIXED_HIGHLIGHTS_SURFACE_Z_INDEX;

/**
 * Placed immediately above the highlights layer. Renders the thick border
 * indicating the single focused cell.
 */
export const FOCUS_LAYER_Z_INDEX = SELECTION_CHROME_LAYER_Z_INDEX + 1;

/**
 * Placed above the focus layer to guarantee the user can always click the
 * fill handle without the focus box's border blocking pointer events.
 */
export const FILL_HANDLE_LAYER_Z_INDEX = SELECTION_CHROME_LAYER_Z_INDEX + 2;

/**
 * The top-most layer. Guarantees the dashed "marching ants" preview box
 * draws cleanly over all other selection UI while the user is dragging.
 */
export const FILL_PREVIEW_LAYER_Z_INDEX = SELECTION_CHROME_LAYER_Z_INDEX + 3;

/**
 * Preserves table stacking when a logical highlight is projected onto the
 * ordinary scrolling overlay below pinned cells.
 */
export function getTableBaseFragmentZIndex({
  mode,
  zIndex
}: Pick<AddHighlightOptions, 'mode' | 'zIndex'>): number {
  if (mode === 'row' || mode === 'container') {
    return zIndex;
  }

  if (zIndex === FILL_PREVIEW_LAYER_Z_INDEX) {
    return SCROLLING_INTERACTION_LAYER_Z_INDEX;
  }

  if (
    zIndex === FIXED_HIGHLIGHTS_LAYER_Z_INDEX ||
    zIndex === FOCUS_LAYER_Z_INDEX ||
    zIndex === FILL_HANDLE_LAYER_Z_INDEX
  ) {
    return SCROLLING_HIGHLIGHTS_LAYER_Z_INDEX;
  }

  return zIndex;
}
